import React, { useState } from 'react';
import { SearchedProductName, DisplayedProduct, WebSearchResult, CncMatchTrace, CncMatchTraceFilter } from '../types/models';
import { ApiService } from '../services/api';
import ProductDetailsModal from './ProductDetailsModal';
import ProductSelectionModal from './ProductSelectionModal'; // Импорт ProductSelectionModal
import WebSearchResultsModal from './WebSearchResultsModal'; // Импорт WebSearchResultsModal
import { GlobeAltIcon, ArrowPathIcon } from '@heroicons/react/24/outline'; // Импорт иконки глобуса и новой иконки

interface SearchedProductCardProps {
  spn: SearchedProductName;
  onUpdate: (updatedSpn: SearchedProductName) => void;
  onStartSearch: (spnId: number) => void;
  isSearching: boolean;
  onViewVariants: (spnId: number) => void;
  isActive: boolean; // Новый проп для отслеживания активного состояния
  userId: number;
  orderNumber?: number; // Добавлено для отображения порядкового номера
}

const SearchedProductCard: React.FC<SearchedProductCardProps> = ({
  spn,
  onUpdate,
  onStartSearch,
  isSearching,
  onViewVariants,
  isActive,
  userId,
  orderNumber
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(spn.name);
  const [isSelectingByVendorCode, setIsSelectingByVendorCode] = useState(false); // Новое состояние для выбора по артикулу
  const [vendorCodeInput, setVendorCodeInput] = useState(''); // Новое состояние для поля ввода артикула
  const [isModalOpen, setIsModalOpen] = useState(false); // Состояние для управления видимостью модального окна
  const [productDetails, setProductDetails] = useState<DisplayedProduct | null>(null); // Состояние для хранения деталей продукта
  const [productsForSelection, setProductsForSelection] = useState<DisplayedProduct[] | null>(null);
  const [isProductSelectionModalOpen, setIsProductSelectionModalOpen] = useState(false);
  const [isWebSearchResultsModalOpen, setIsWebSearchResultsModalOpen] = useState(false); // Состояние для модального окна веб-поиска
  const [isWebSearchLoading, setIsWebSearchLoading] = useState(false); // Состояние для индикатора загрузки веб-поиска
  const [matchTrace, setMatchTrace] = useState<CncMatchTrace | null | undefined>(undefined);
  const [isMatchTraceLoading, setIsMatchTraceLoading] = useState(false);
  const [matchTraceError, setMatchTraceError] = useState<string | null>(null);
  const [selectingCncCandidateId, setSelectingCncCandidateId] = useState<number | null>(null);
  const [cncCandidateSelectionError, setCncCandidateSelectionError] = useState<string | null>(null);
  const [webSelectionState, setWebSelectionState] = useState<{ activeArticle: string | null; isSubmitting: boolean; error: string | null }>({
    activeArticle: null,
    isSubmitting: false,
    error: null,
  });

  const closeWebSearchModal = () => {
    setIsWebSearchResultsModalOpen(false);
    setWebSelectionState({ activeArticle: null, isSubmitting: false, error: null });
  };

  const handleWebSearchClick = async (e: React.MouseEvent) => {
    e.stopPropagation(); // Предотвращаем срабатывание handleCardClick

    setWebSelectionState({ activeArticle: null, isSubmitting: false, error: null });
    setIsWebSearchResultsModalOpen(true); // Открываем модальное окно сразу

    if (!(spn.web_search_results && spn.web_search_results.length > 0)) {
      // Если результатов еще нет, запускаем веб-поиск
      setIsWebSearchLoading(true); // Включаем индикатор загрузки
      try {
        const updatedSpn = await ApiService.triggerWebSearch(spn.id);
        const spnWithResults: SearchedProductName = {
          ...updatedSpn,
          orderNumber: spn.orderNumber, // Сохраняем локальный orderNumber, если он есть и важен
        };
        onUpdate(spnWithResults); // Обновляем SPN в родительском компоненте
      } catch (error) {
        console.error('Failed to trigger web search:', error);
        alert('Не удалось запустить веб-поиск. Пожалуйста, попробуйте еще раз.');
      } finally {
        setIsWebSearchLoading(false); // Выключаем индикатор загрузки
      }
    }
  };

  const handleWebResultSelect = async (result: WebSearchResult) => {
    if (!result.article || !result.article.trim()) {
      setWebSelectionState({ activeArticle: null, isSubmitting: false, error: 'Выбор невозможен: отсутствует артикул' });
      return;
    }

    setWebSelectionState({ activeArticle: result.article, isSubmitting: true, error: null });
    try {
      const updatedSpn = await ApiService.selectWebSearchResult(spn.id, result);
      const updatedSpnWithOrder: SearchedProductName = {
        ...updatedSpn,
        orderNumber: spn.orderNumber,
      };
      onUpdate(updatedSpnWithOrder);
      closeWebSearchModal();
    } catch (error) {
      console.error('Failed to apply web search selection:', error);
      const message = error instanceof Error ? error.message : 'Не удалось применить результат веб-поиска';
      setWebSelectionState({ activeArticle: null, isSubmitting: false, error: message });
    } finally {
      setWebSelectionState((prev) => ({ ...prev, isSubmitting: false, activeArticle: null }));
    }
  };

  const handleDetermineCategoryClick = async (e: React.MouseEvent) => {
    e.stopPropagation(); // Предотвращаем срабатывание handleCardClick
    
    try {
      // Вызываем новый метод API для определения категории
      await ApiService.determineCategoryForSpn(spn.id, userId);
      
      // Переводим SPN в состояние поиска (для вебсокета)
      const searchingSpn = {
        ...spn,
        is_searching: true // Добавляем флаг, показывающий что идет поиск
      };
      
      onUpdate(searchingSpn); // Обновляем SPN в родительском компоненте
      onStartSearch(spn.id); // Сигнализируем AppContext, что для этого SPN начался поиск
    } catch (error) {
      console.error('Failed to determine category:', error);
      alert('Не удалось определить категорию. Пожалуйста, попробуйте еще раз.');
    }
  };

  // Обработчик клика по карточке
  const handleCardClick = (e: React.MouseEvent) => {
    // Проверяем, что клик не по кнопке или полю ввода, и не в режиме выбора по артикулу
    if (
      !(e.target as HTMLElement).closest('button') &&
      !(e.target as HTMLElement).closest('input') &&
      !(e.target as HTMLElement).closest('textarea') &&
      !isEditing &&
      !isSelectingByVendorCode // Добавляем проверку на режим выбора по артикулу
    ) {
      onViewVariants(spn.id);
    }
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation(); // Предотвращаем срабатывание handleCardClick
    setIsEditing(true);
    setEditName(spn.name);
  };

  const handleCancel = (e: React.MouseEvent) => {
    e.stopPropagation(); // Предотвращаем срабатывание handleCardClick
    setIsEditing(false);
  };

  const handleSave = async (e: React.MouseEvent) => {
    e.stopPropagation(); // Предотвращаем срабатывание handleCardClick
    
    // Проверяем, было ли имя изменено
    if (editName.trim() === spn.name.trim()) {
      // Если имя не изменилось, просто закрываем режим редактирования без обращения к API
      setIsEditing(false);
      return;
    }
    
    // Проверяем, не пустое ли имя
    if (editName.trim() === '') {
      // Можно добавить сообщение об ошибке
      alert('Имя не может быть пустым');
      return;
    }
    
    try {
      // Отправляем запрос только если имя действительно изменилось
      const updatedSpn = await ApiService.updateSPNName(spn.id, userId, {
        name: editName.trim()
      });
      
      // Переводим SPN в состояние поиска
      const searchingSpn = {
        ...spn,
        name: updatedSpn.name, // Обновляем имя SPN
        is_searching: true // Добавляем флаг, показывающий что идет поиск
      };
      
      // Обновляем состояние в родительском компоненте
      onUpdate(searchingSpn);
      // Сигнализируем AppContext, что для этого SPN начался поиск
      onStartSearch(spn.id);
      setIsEditing(false);
      
    } catch (error) {
      console.error('Failed to update SPN:', error);
      // Можно добавить отображение ошибки пользователю
    }
  };

  const handleDeselectProduct = async (e: React.MouseEvent) => {
    e.stopPropagation(); // Предотвращаем срабатывание handleCardClick
    
    try {
      // Вызов API для отмены выбора продукта
      await ApiService.deselectProduct(spn.id);
      
      // Создаем обновленный объект SPN без выбранного продукта
      const updatedSpn: SearchedProductName = {
        ...spn,
        selected_product_code: null,
        selected_product_details: null,
        vendor_match: null,
      };
      
      // Обновляем состояние в родительском компоненте
      onUpdate(updatedSpn);
    } catch (error) {
      console.error('Failed to deselect product:', error);
    }
  };

  const loadMatchTrace = async () => {
    if (isMatchTraceLoading || matchTrace !== undefined) {
      return;
    }
    setIsMatchTraceLoading(true);
    setMatchTraceError(null);
    try {
      const trace = await ApiService.getCncMatchTrace(spn.id);
      setMatchTrace(trace);
    } catch (error) {
      console.error('Failed to load CNC match trace:', error);
      setMatchTraceError('Не удалось загрузить детали подбора');
    } finally {
      setIsMatchTraceLoading(false);
    }
  };

  const handleMatchTraceToggle = (e: React.SyntheticEvent<HTMLDetailsElement>) => {
    e.stopPropagation();
    if (e.currentTarget.open) {
      loadMatchTrace();
    }
  };

  const handleCncCandidateSelect = async (candidateId: number) => {
    if (selectingCncCandidateId !== null || matchTrace?.selected_cnc_product_id === candidateId) {
      return;
    }

    setSelectingCncCandidateId(candidateId);
    setCncCandidateSelectionError(null);
    try {
      const result = await ApiService.selectCncCandidate(spn.id, candidateId);
      onUpdate({ ...result.spn, orderNumber: spn.orderNumber });
      setMatchTrace(result.trace);
    } catch (error) {
      console.error('Failed to select CNC candidate:', error);
      setCncCandidateSelectionError(
        error instanceof Error ? error.message : 'Не удалось выбрать CNC-аналог'
      );
    } finally {
      setSelectingCncCandidateId(null);
    }
  };

  const formatTraceValue = (item: CncMatchTraceFilter) => {
    if (item.value_raw !== null && item.value_raw !== undefined && item.value_raw !== '') {
      return item.value_raw;
    }
    if (item.value_number !== null && item.value_number !== undefined) {
      return String(item.value_number);
    }
    return '-';
  };

  const formatConfidence = (value?: number | null) => {
    if (value === null || value === undefined) {
      return '-';
    }
    return `${Math.round(value * 100)}%`;
  };

  const statusClassName = (status?: string) => {
    if (status === 'matched') {
      return 'bg-green-100 text-green-800 border-green-200';
    }
    if (status === 'error') {
      return 'bg-red-100 text-red-800 border-red-200';
    }
    return 'bg-amber-100 text-amber-800 border-amber-200';
  };

  const handleShowDetails = async () => {
    if (spn.selected_product_code) {
      // Проверяем, если у продукта есть source_url, открываем его в новой вкладке
      if (spn.selected_product_details?.source_url) {
        window.open(spn.selected_product_details.source_url, '_blank');
        return;
      }

      // Если source_url нет, загружаем детали через API как раньше
      try {
        const details = await ApiService.getProductDetails(spn.selected_product_code);
        setProductDetails(details);
        setIsModalOpen(true); // Открываем модальное окно только после успешной загрузки деталей
      } catch (error) {
        console.error('Ошибка при загрузке деталей продукта:', error);
        setProductDetails(null);
        setIsModalOpen(true); // Открываем модальное окно для отображения ошибки или пустого состояния
      }
    } else {
      console.warn('No selected_product_code available to fetch details.');
      setProductDetails(null);
      setIsModalOpen(true); // Открываем модальное окно с сообщением, что код продукта не выбран
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setProductDetails(null); // Очистить детали при закрытии
  };

  const getStatusInfo = () => {
    if (isSearching) {
      return (
        <div className="flex items-center text-blue-500">
            <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Поиск...
        </div>
      );
    }
    
    if (spn.etim_features && spn.etim_features.length > 0) {
      return (
        <div className="text-xs text-green-500">
          {`Найдено характеристик: ${spn.etim_features.length}`}
        </div>
      );
    }
    
    if (spn.category) { // Если категория определена, но характеристик нет
      return (
        <div className="text-xs text-green-500">
          Категория определена. Характеристики отсутствуют.
        </div>
      );
    }

    return (
      <div className="text-xs text-yellow-500">
        Отсутствуют характеристики. Выполните повторный поиск.
      </div>
    );
  };

  return (
    <div 
      className={`bg-white border rounded-lg p-2 mb-2 transition-all duration-200 ${
        isActive ? 'border-10 border-blue-700 ring-2 ring-blue-500' : 'hover:border-blue-300 hover:shadow-md'
      } ${!isEditing ? 'cursor-pointer' : ''}`}
      onClick={handleCardClick}
    >
      <div>
        <div className="flex justify-between items-start mb-2">
          {/* Режим редактирования имени SPN */}
          {isEditing ? (
            <div className="flex items-center w-full">
              <div className="flex-grow mr-2 relative">
                <textarea
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="w-full text-sm font-semibold border border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 p-1 resize-none overflow-hidden"
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    minHeight: '2rem',
                    height: 'auto'
                  }}
                  rows={1}
                  onInput={(e) => {
                    // Автоматически подстраиваем высоту textarea под содержимое
                    const target = e.target as HTMLTextAreaElement;
                    target.style.height = 'auto';
                    target.style.height = `${target.scrollHeight}px`;
                  }}
                  ref={(textareaRef) => {
                    // При первой отрисовке устанавливаем высоту textarea
                    if (textareaRef) {
                      textareaRef.style.height = 'auto';
                      textareaRef.style.height = `${textareaRef.scrollHeight}px`;
                    }
                  }}
                />
              </div>
              <div className="flex space-x-1 flex-shrink-0">
                {/* Кнопка Сохранить с иконкой */}
                <button
                  onClick={handleSave}
                  className="p-1 bg-green-600 hover:bg-green-700 text-white rounded-md"
                  title="Сохранить"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </button>
                {/* Кнопка Отмена с иконкой */}
                <button
                  onClick={handleCancel}
                  className="p-1 bg-gray-500 hover:bg-gray-600 text-white rounded-md"
                  title="Отмена"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          ) : (
            // Отображение имени SPN и кнопки редактирования
            <>
              <div className="flex items-center">
                {orderNumber !== undefined && (
                  <span className="text-sm font-medium text-gray-500 mr-2">{orderNumber}.</span>
                )}
                <h3 className="text-sm font-semibold">{spn.name}</h3>
              </div>
              {!isEditing && (
                <button
                  onClick={handleEdit}
                  className="text-blue-600 hover:text-blue-800 ml-2"
                  title="Редактировать имя и перезапустить базовый поиск"
                  data-testid="edit-recherche-button"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
              )}
            </>
          )}
        </div>

        <div className="mb-1">
          {getStatusInfo()}
        </div>

        {/* Блок выбора продукта по артикулу */}
        { !isEditing && ( // Показываем только если продукт не выбран и не в режиме редактирования имени
          <div className="mb-1">
            {!isSelectingByVendorCode ? (
              <div className="flex justify-between items-start w-full"> {/* Обертка для горизонтального расположения */}
                <div className="flex flex-col items-start">
                  {/* Кнопка "Выбрать по артикулу" */}
                  <button data-testid="select-by-article-button"
                    onClick={(e) => {
                      e.stopPropagation(); // Предотвращаем срабатывание handleCardClick
                      setIsSelectingByVendorCode(true);
                    }}
                    className="text-blue-600 hover:text-blue-800 text-xs font-medium mb-1" // Добавлен mb-1 для отступа, если есть ссылка ниже
                  >
                    {spn.selected_product_code ? "Выбрать другой продукт по артикулу" : "Выбрать по артикулу"}
                  </button>
                  {/* Новая гиперссылка */}
                  {spn.article && spn.article.trim() !== '' && (
                    <a
                      href="#"
                      onClick={async (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (spn.article) { // Дополнительная проверка на случай, если article станет null/undefined между рендером и кликом
                          try {
                            const foundProducts = await ApiService.selectProductByVendorCode(spn.id, spn.article);
                            if (foundProducts && foundProducts.length === 1) {
                              // const selectedProduct = foundProducts[0]; // Не используется напрямую, т.к. getSPN вернет обновленные данные
                              var updated_spn_data = await ApiService.getSPN(spn.id);
                              const updatedSpnFromApi: SearchedProductName = {
                                ...updated_spn_data, // Берем все данные с сервера
                                orderNumber: spn.orderNumber, // Сохраняем локальный orderNumber, если он есть и важен
                              };
                              onUpdate(updatedSpnFromApi);
                              // Сбрасывать isSelectingByVendorCode и vendorCodeInput не нужно, т.к. это прямой выбор
                            } else if (foundProducts && foundProducts.length > 1) {
                              setProductsForSelection(foundProducts);
                              setIsProductSelectionModalOpen(true);
                            } else {
                              alert(`Продукты с артикулом ${spn.article} не найдены.`);
                            }
                          } catch (error) {
                            console.error('Failed to select product by spn.article:', error);
                            alert(`Не удалось найти продукт по артикулу ${spn.article}.`);
                          }
                        }
                      }}
                      className="text-blue-600 hover:text-blue-800 text-xs font-medium"
                    >
                      Выбрать продукт по артикулу {spn.article} полученному из строки запроса
                    </a>
                  )}
                </div>
                <div className="flex flex-col items-start space-y-2"> {/* Обертка для вертикального расположения кнопок поиска */}
                  {/* Новая кнопка "Повторить поиск" */}
                  {!isEditing && !isSelectingByVendorCode && (
                    <button
                      data-testid="research-button"
                      title="Перезапустить поиск с определнием категории и характеристик"
                      onClick={handleDetermineCategoryClick}
                      className="text-blue-600 hover:text-blue-800 text-xs font-medium flex items-center"
                      disabled={isSearching} // Отключаем кнопку во время загрузки или поиска
                    >
                      {isSearching ? (
                        <>
                          <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Определение категории...
                        </>
                      ) : (
                        <>
                          <ArrowPathIcon className="h-4 w-4 mr-1" /> {/* Иконка всегда отображается */}
                          Повторить поиск
                        </>
                      )}
                    </button>
                  )}
                  {/* Новая ссылка/кнопка для веб-поиска */}
                  <button
                    data-testid="web-scrapper-button"
                    onClick={handleWebSearchClick}
                    title={spn.web_search_results && spn.web_search_results.length > 0 ? ('Посмотреть результаты поиска артикула в интернете') : ('Запустить поиск артикула в интернете')}
                    className="text-blue-600 hover:text-blue-800 text-xs font-medium flex items-center"
                    disabled={isWebSearchLoading} // Отключаем кнопку во время загрузки
                  >
                    {isWebSearchLoading ? (
                      <>
                        <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Загрузка...
                      </>
                    ) : (
                      <>
                        <GlobeAltIcon className="h-4 w-4 mr-1" /> {/* Иконка всегда отображается */}
                        {spn.web_search_results && spn.web_search_results.length > 0 ? (
                          "Посмотреть результаты веб-поиска"
                        ) : (
                          "Запустить веб-поиск"
                        )}
                      </>
                    )}
                  </button>
                  
                </div>
              </div>
            ) : (
              // Поле ввода артикула и кнопки "Найти" / "Отмена"
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  value={vendorCodeInput}
                  onChange={(e) => setVendorCodeInput(e.target.value)}
                  placeholder="Введите артикул"
                  className="flex-grow text-xs border border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 p-1"
                  onClick={(e) => e.stopPropagation()} // Предотвращаем срабатывание handleCardClick
                />
                <button
                  onClick={async (e) => {
                    e.stopPropagation(); // Предотвращаем срабатывание handleCardClick
                    if (vendorCodeInput.trim()) {
                      try {
                        const foundProducts = await ApiService.selectProductByVendorCode(spn.id, vendorCodeInput.trim());
                        
                        if (foundProducts && foundProducts.length === 1) {
                          const selectedProduct = foundProducts[0];
                          var updated_spn = await ApiService.getSPN(spn.id); // Обновляем SPN с сервера, чтобы получить актуальные данные
                          const updatedSpn: SearchedProductName = {
                            ...updated_spn,
                            orderNumber: spn.orderNumber, // Сохраняем порядковый номер, если он есть
                            //selected_product_code: selectedProduct.code,
                            //selected_product_details: selectedProduct,
                          };
                          onUpdate(updatedSpn);
                          setIsSelectingByVendorCode(false);
                          setVendorCodeInput('');
                        } else if (foundProducts && foundProducts.length > 1) {
                          setProductsForSelection(foundProducts);
                          setIsProductSelectionModalOpen(true);
                          // Не сбрасываем isSelectingByVendorCode и vendorCodeInput здесь,
                          // чтобы пользователь видел, какой артикул он вводил.
                          // Сброс будет в handleProductSelectionFromModal или handleCloseProductSelectionModal
                        } else {
                          alert('Продукты с таким артикулом не найдены.');
                          // Оставляем режим выбора по артикулу активным, чтобы пользователь мог исправить ввод
                        }
                      } catch (error) {
                        console.error('Failed to select product by vendor code:', error);
                        alert('Не удалось найти продукт по указанному артикулу.');
                        // Оставляем режим выбора по артикулу активным
                      }
                    } else {
                      alert('Пожалуйста, введите артикул.');
                    }
                  }}
                  className="p-1 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-xs flex-shrink-0"
                >
                  Найти
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation(); // Предотвращаем срабатывание handleCardClick
                    setIsSelectingByVendorCode(false); // Скрываем поле ввода
                    setVendorCodeInput(''); // Сбрасываем поле ввода
                  }}
                  className="p-1 bg-gray-500 hover:bg-gray-600 text-white rounded-md text-xs flex-shrink-0"
                >
                  Отмена
                </button>
              </div>
            )}
          </div>
        )}

        {/* Блок выбранного продукта */}

        {spn.selected_product_code && (
          <div className="bg-green-50 border border-green-200 rounded p-2 mb-3">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <p className="text-xs font-medium text-green-800">
                  Выбранный продукт: {spn.selected_product_details?.description}
                  <br/>
                  Производитель: {spn.selected_product_details?.brand}, арт. {spn.selected_product_details?.vendor_code}, цена {spn.selected_product_details?.price} руб., количество {spn.quantity}, сумма {spn.selected_product_details?.price && spn.quantity ? (spn.selected_product_details.price * (spn.quantity || 1)).toFixed(2) : '0.00'} руб.
                </p>
                {/* Пометка для продуктов из интернета */}
                {spn.selected_product_details?.source_url && (
                  <div className="flex items-center mt-1">
                    <GlobeAltIcon className="h-3 w-3 text-blue-500 mr-1" />
                    <span className="text-xs text-blue-600 font-medium">Найден в интернете</span>
                  </div>
                )}
                {spn.vendor_match && (
                  <div className="mt-2 border-t border-green-200 pt-2 text-xs text-green-900">
                    <div className="font-medium">
                      Аналог поставщика {spn.vendor_match.label}: {spn.vendor_match.name || 'без наименования'}
                    </div>
                    <div className="mt-0.5 text-green-800">
                      Арт. {spn.vendor_match.vendor_code || '-'}
                      {spn.vendor_match.type_item_name ? `, тип: ${spn.vendor_match.type_item_name}` : ''}
                      {spn.vendor_match.series ? `, серия: ${spn.vendor_match.series}` : ''}
                    </div>
                    <details className="mt-1" onClick={(e) => e.stopPropagation()}>
                      <summary className="cursor-pointer text-green-700 underline underline-offset-2">
                        Характеристики аналога
                      </summary>
                      {spn.vendor_match.description && spn.vendor_match.description !== spn.vendor_match.name && (
                        <div className="mt-1 rounded border border-green-100 bg-white/70 px-2 py-1 text-green-800">
                          {spn.vendor_match.description}
                        </div>
                      )}
                      {spn.vendor_match.specs && spn.vendor_match.specs.length > 0 ? (
                        <div className="mt-1 max-h-44 overflow-y-auto rounded border border-green-100 bg-white/70">
                          <table className="min-w-full text-left">
                            <tbody>
                              {spn.vendor_match.specs.map((spec, index) => (
                                <tr key={`${spec.name}-${index}`} className="border-b border-green-50 last:border-b-0">
                                  <td className="w-1/2 px-2 py-1 text-green-900">{spec.name}</td>
                                  <td className="px-2 py-1 text-green-800">{spec.value || '-'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="mt-1 text-green-700">Характеристики не переданы</div>
                      )}
                    </details>
                  </div>
                )}
                <details className="mt-2 border-t border-green-200 pt-2 text-xs text-slate-800" onToggle={handleMatchTraceToggle} onClick={(e) => e.stopPropagation()}>
                  <summary className="cursor-pointer text-green-700 underline underline-offset-2">
                    Детали подбора
                  </summary>
                  <div className="mt-2 rounded border border-slate-200 bg-white/80 p-2">
                    {isMatchTraceLoading && (
                      <div className="space-y-2">
                        <div className="h-4 w-1/3 animate-pulse rounded bg-slate-200" />
                        <div className="h-4 w-2/3 animate-pulse rounded bg-slate-200" />
                        <div className="h-16 animate-pulse rounded bg-slate-100" />
                      </div>
                    )}
                    {matchTraceError && !isMatchTraceLoading && (
                      <div className="text-red-600">{matchTraceError}</div>
                    )}
                    {matchTrace === null && !isMatchTraceLoading && !matchTraceError && (
                      <div className="text-slate-500">
                        Детали не найдены: CNC-подбор для этой позиции еще не запускался
                      </div>
                    )}
                    {matchTrace && !isMatchTraceLoading && (
                      <div className="space-y-3">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className={`rounded-full border px-2 py-0.5 font-medium ${statusClassName(matchTrace.status)}`}>
                            {matchTrace.status_label}
                          </span>
                        </div>

                        {matchTrace.type_item_name && (
                          <div className="space-y-2">
                            <div className="text-[11px] uppercase tracking-wide text-slate-500">Категория CNC</div>
                            <div className="font-medium text-slate-950">{matchTrace.type_item_name}</div>
                            <div className="flex flex-wrap items-center gap-2">
                              {matchTrace.type_item_confidence !== null && matchTrace.type_item_confidence !== undefined && (
                                <span className="rounded-full border border-violet-100 bg-violet-50 px-2 py-0.5 font-medium text-violet-800">
                                  Уверенность в выборе категории: {formatConfidence(matchTrace.type_item_confidence)}
                                </span>
                              )}
                            </div>
                            {matchTrace.type_item_reason && (
                              <div>
                                <div className="mb-1 text-[11px] uppercase tracking-wide text-slate-500">Причина выбора категории</div>
                                <div className="rounded border border-violet-100 bg-violet-50 px-2 py-1 text-violet-900">
                                  {matchTrace.type_item_reason}
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {matchTrace.failure_reason_label && (
                          <div className="rounded border border-amber-200 bg-amber-50 px-2 py-1 text-amber-900">
                            {matchTrace.failure_reason_label}
                          </div>
                        )}

                        {matchTrace.error && (
                          <div className="rounded border border-red-200 bg-red-50 px-2 py-1 text-red-800">
                            {matchTrace.error}
                          </div>
                        )}

                        {matchTrace.match_strategy_label && (
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="rounded-full border border-blue-100 bg-blue-50 px-2 py-0.5 font-medium text-blue-800">
                              {matchTrace.match_strategy_label}
                            </span>
                          </div>
                        )}

                        {matchTrace.filters.length > 0 && (
                          <div>
                            <div className="mb-1 font-medium text-slate-900">Примененные фильтры</div>
                            <div className="grid gap-1 sm:grid-cols-2">
                              {matchTrace.filters.map((filter, index) => (
                                <div key={`${filter.spec_id || filter.spec_name}-${index}`} className="rounded border border-slate-100 bg-slate-50 px-2 py-1">
                                  <div className="text-slate-500">{filter.spec_name}</div>
                                  <div className="font-medium text-slate-900">{formatTraceValue(filter)}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {matchTrace.relaxations.length > 0 && (
                          <div>
                            <div className="mb-1 font-medium text-slate-900">Корректировки</div>
                            <div className="space-y-1">
                              {matchTrace.relaxations.map((relaxation, index) => (
                                <div key={`${relaxation.spec_id || relaxation.spec_name}-${index}`} className="rounded border border-indigo-100 bg-indigo-50 px-2 py-1 text-indigo-900">
                                  {relaxation.spec_name}: {relaxation.from_value_raw ?? relaxation.from_value_number ?? '-'} → {relaxation.to_value_raw ?? relaxation.to_value_number ?? '-'}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {matchTrace.dropped_filters.length > 0 && (
                          <div>
                            <div className="mb-1 font-medium text-slate-900">Исключенные фильтры</div>
                            <div className="space-y-1">
                              {matchTrace.dropped_filters.map((filter, index) => (
                                <div key={`${filter.spec_id || filter.spec_name}-${index}`} className="rounded border border-amber-100 bg-amber-50 px-2 py-1 text-amber-900">
                                  {filter.spec_name}: {formatTraceValue(filter)}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {matchTrace.confidence !== null && matchTrace.confidence !== undefined && (
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 font-medium text-slate-700">
                              Уверенность в выборе кандидата: {formatConfidence(matchTrace.confidence)}
                            </span>
                          </div>
                        )}

                        {matchTrace.decision_reason && (
                          <div className="rounded border border-blue-100 bg-blue-50 px-2 py-1 text-blue-900">
                            <div className="mb-0.5 text-[11px] uppercase tracking-wide text-blue-500">Причина выбора кандидата</div>
                            {matchTrace.decision_reason}
                          </div>
                        )}

                        <div>
                          <div className="mb-1 flex items-center justify-between">
                            <span className="font-medium text-slate-900">Кандидаты</span>
                            <span className="text-slate-500">{matchTrace.candidates_count}</span>
                          </div>
                          {matchTrace.candidates.length > 0 ? (
                            <div className="max-h-44 overflow-y-auto rounded border border-slate-100">
                              {matchTrace.candidates.map((candidate) => (
                                <button
                                  type="button"
                                  key={candidate.id}
                                  disabled={candidate.selected || selectingCncCandidateId !== null}
                                  onClick={() => handleCncCandidateSelect(candidate.id)}
                                  className={`block w-full border-b border-slate-100 px-2 py-1 text-left last:border-b-0 ${candidate.selected ? 'cursor-default bg-green-50 text-green-900' : 'bg-white text-slate-800 hover:bg-blue-50 disabled:cursor-wait disabled:opacity-60'}`}
                                >
                                  <div className="flex items-start justify-between gap-2">
                                    <div>
                                      <div className="font-medium">{candidate.name || 'Без наименования'}</div>
                                      <div className="text-slate-500">Арт. {candidate.vendor_code || '-'}</div>
                                    </div>
                                    {candidate.selected ? (
                                      <span className="rounded bg-green-100 px-1.5 py-0.5 text-[11px] font-medium text-green-800">Выбран</span>
                                    ) : selectingCncCandidateId === candidate.id ? (
                                      <span className="rounded bg-blue-100 px-1.5 py-0.5 text-[11px] font-medium text-blue-800">Сохраняем...</span>
                                    ) : (
                                      <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium text-slate-700">Выбрать</span>
                                    )}
                                  </div>
                                </button>
                              ))}
                            </div>
                          ) : (
                            <div className="rounded border border-slate-100 bg-slate-50 px-2 py-1 text-slate-500">
                              Кандидаты не сохранены
                            </div>
                          )}
                          {cncCandidateSelectionError && (
                            <div className="mt-1 rounded border border-red-200 bg-red-50 px-2 py-1 text-red-700">
                              {cncCandidateSelectionError}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </details>
              </div>
              <div className="flex items-center"> {/* Контейнер для кнопок */}
                {!isEditing && (
                  <button
                    onClick={handleShowDetails}
                    className="px-2 py-0.5 mr-2 rounded text-xs bg-gray-300 hover:bg-gray-400 text-gray-800 flex-shrink-0" // Стилизация кнопки "Детали"
                    title={spn.selected_product_details?.source_url ? "Открыть страницу товара в интернете" : "Детали продукта"}
                  >
                    Детали
                  </button>
                )}
                {!isEditing && (
                  <button
                    onClick={handleDeselectProduct}
                    className="ml-1 flex-shrink-0 bg-red-500 hover:bg-red-600 text-white rounded-full w-4 h-4 flex items-center justify-center items-center self-center"
                    title="Отменить выбор продукта"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
        {spn.category && spn.category.name && (
          <div className="mt-2 text-xs text-gray-500">
            Категория: {spn.category.level3_category?.name} Подкатегория: {spn.category.name}
          </div>
        )}
      </div>
      {isModalOpen && productDetails && ( // Отображаем модальное окно, если isModalOpen true и есть productDetails
        <ProductDetailsModal
          productDetails={productDetails}
          onClose={handleCloseModal}
        />
      )}
      {productsForSelection && (
        <ProductSelectionModal
          products={productsForSelection}
          isOpen={isProductSelectionModalOpen}
          onSelect={async (selectedProduct: DisplayedProduct) => {
            try {
              var updated_spn = await ApiService.selectProduct(spn.id, selectedProduct.code);
              const updatedSpn: SearchedProductName = {
                ...updated_spn,
                orderNumber: spn.orderNumber,
                selected_product_details: selectedProduct,
              };
              onUpdate(updatedSpn);
            } catch (error) {
              console.error('Failed to confirm product selection:', error);
              alert('Не удалось подтвердить выбор продукта.');
            } finally {
              setIsProductSelectionModalOpen(false);
              setProductsForSelection(null);
              setIsSelectingByVendorCode(false);
              setVendorCodeInput('');
            }
          }}
          onClose={() => {
            setIsProductSelectionModalOpen(false);
            setProductsForSelection(null);
            // Не сбрасываем isSelectingByVendorCode и vendorCodeInput здесь,
            // чтобы пользователь мог продолжить ввод или отменить его явно.
            // Если нужно полное закрытие режима выбора по артикулу, то:
            // setIsSelectingByVendorCode(false);
            // setVendorCodeInput('');
          }}
        />
      )}

      {/* Модальное окно для отображения результатов веб-поиска */}
      {isWebSearchResultsModalOpen && (
        <WebSearchResultsModal
          results={spn.web_search_results || []} // Передаем пустой массив, если результатов нет
          onClose={closeWebSearchModal}
          isLoading={isWebSearchLoading}
          onSelect={handleWebResultSelect}
          selectionState={webSelectionState}
        />
      )}
    </div>
  );
};

export default SearchedProductCard;
